import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Clock,
  CurrencyGbp,
  Vault,
  Plus,
  Receipt,
  CircleNotch,
  ArrowRight,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ActivityModal } from '@/components/activities/ActivityModal';
import { fetchMatterFinancialSummary } from '@/lib/mattersApi';
import type { ActivityType } from '@/lib/activitiesApi';
import { useAuthStore } from '@/features/auth/store';
import { canLogActivity } from '@/lib/activityAccess';

interface Props {
  matterId: string;
  /** Click on Outstanding widget jumps the parent to the Fees tab. */
  onViewOutstanding?: () => void;
}

function fmtMoney(value: number, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

function fmtDuration(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function MatterFinancialWidgets({ matterId, onViewOutstanding }: Props) {
  const user = useAuthStore((s) => s.user);
  const role = user?.memberType;
  const basePath = role === 'admin'
    ? '/admin'
    : role === 'senior_clerk' || role === 'junior_clerk'
      ? '/clerk'
      : '/barrister';

  const [activityModalType, setActivityModalType] = useState<ActivityType | null>(null);

  const summaryQ = useQuery({
    queryKey: ['matter-financial-summary', matterId],
    queryFn: () => fetchMatterFinancialSummary(matterId),
    refetchOnWindowFocus: true,
  });

  return (
    <>
      <Card className="overflow-hidden">
        <div className="grid divide-y divide-border md:grid-cols-3 md:divide-x md:divide-y-0">
          {/* WIP */}
          <div className="px-5 py-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <Clock size={13} weight="duotone" /> Work in progress
            </div>
            {summaryQ.isLoading ? (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <CircleNotch size={11} className="animate-spin" /> Loading
              </div>
            ) : summaryQ.data ? (
              <>
                <div className="mt-1 text-2xl font-semibold text-foreground">
                  {fmtMoney(summaryQ.data.wip.totalAmount, summaryQ.data.currency)}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {summaryQ.data.wip.totalEntries === 0
                    ? 'No unbilled entries yet'
                    : `${summaryQ.data.wip.totalEntries} unbilled · ${fmtDuration(summaryQ.data.wip.totalMinutes)}`}
                </div>
              </>
            ) : (
              <div className="mt-2 text-xs text-red-600 dark:text-red-400">Could not load</div>
            )}
          </div>

          {/* Outstanding */}
          <div className="px-5 py-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <CurrencyGbp size={13} weight="duotone" /> Outstanding balance
            </div>
            {summaryQ.isLoading ? (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <CircleNotch size={11} className="animate-spin" /> Loading
              </div>
            ) : summaryQ.data ? (
              <>
                <div
                  className={`mt-1 text-2xl font-semibold ${
                    summaryQ.data.outstanding > 0
                      ? 'text-amber-700 dark:text-amber-300'
                      : 'text-emerald-700 dark:text-emerald-300'
                  }`}
                >
                  {fmtMoney(summaryQ.data.outstanding, summaryQ.data.currency)}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs">
                  <span className="text-muted-foreground">
                    {summaryQ.data.outstanding === 0
                      ? 'No open balance'
                      : `${fmtMoney(summaryQ.data.billed, summaryQ.data.currency)} billed`}
                  </span>
                  {summaryQ.data.outstanding > 0 && onViewOutstanding && (
                    <button
                      onClick={onViewOutstanding}
                      className="inline-flex items-center gap-0.5 font-medium text-brand-600 dark:text-brand-400 hover:underline"
                    >
                      View <ArrowRight size={9} weight="bold" />
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="mt-2 text-xs text-red-600 dark:text-red-400">Could not load</div>
            )}
          </div>

          {/* Trust */}
          <div className="px-5 py-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <Vault size={13} weight="duotone" /> Trust accounts
            </div>
            {summaryQ.isLoading ? (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <CircleNotch size={11} className="animate-spin" /> Loading
              </div>
            ) : summaryQ.data ? (
              <>
                <div className="mt-1 text-2xl font-semibold text-foreground">
                  {summaryQ.data.trust.accountCount}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {summaryQ.data.trust.accountCount === 0 ? (
                    <>
                      None configured ·{' '}
                      <Link
                        to={`${basePath}/bank-accounts`}
                        className="font-medium text-brand-600 dark:text-brand-400 hover:underline"
                      >
                        Set up
                      </Link>
                    </>
                  ) : (
                    <>
                      Active accounts ·{' '}
                      <Link
                        to={`${basePath}/bank-accounts`}
                        className="font-medium text-brand-600 dark:text-brand-400 hover:underline"
                      >
                        Manage
                      </Link>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="mt-2 text-xs text-red-600 dark:text-red-400">Could not load</div>
            )}
          </div>
        </div>

        {/* Quick actions strip */}
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border bg-muted/30 px-5 py-2">
          <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Quick actions
          </span>
          {/* A pupil reads this ledger but cannot write to it — see ActivitiesPage for the note. */}
          {canLogActivity(role) && (
            <>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Clock className="h-3 w-3" />}
                onClick={() => setActivityModalType('time')}
              >
                Add time
              </Button>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Receipt className="h-3 w-3" />}
                onClick={() => setActivityModalType('expense')}
              >
                Add expense
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Plus className="h-3 w-3" />}
            onClick={onViewOutstanding}
            disabled={!onViewOutstanding}
          >
            View bills
          </Button>
        </div>
      </Card>

      {activityModalType && (
        <ActivityModal
          matterId={matterId}
          defaultType={activityModalType}
          onClose={() => setActivityModalType(null)}
          onSaved={() => setActivityModalType(null)}
        />
      )}
    </>
  );
}
